/**
 * calculateWeeklySum
 *
 * Calculates the total hours booked by an employee for a specific week.
 * Used in the month view to display weekly totals.
 *
 * When a project filter is active, only counts hours from matching projects.
 * This matches Vue behavior where weekSum uses bookedProjectHours().
 *
 * @example
 * ```ts
 * const weekStart = new Date('2024-01-01'); // Monday
 * const weeklyHours = calculateWeeklySum(
 *   employee,
 *   weekStart,
 *   bookingsByEmployee,
 *   selectedProjects // Optional: filter by these projects
 * );
 * // Returns: 37.5
 * ```
 */

import type {
  IBooking,
  IServiceEmployee,
  IServiceProject,
} from "@/services/types";
import { endOfWeek, startOfWeek } from "date-fns";

/**
 * Calculate total hours booked by an employee in a specific week.
 * When projectFilter is active, only count hours from matching projects.
 *
 * @param employee - The employee to calculate hours for
 * @param weekStartDate - Any date within the target week
 * @param bookingsByEmployee - Map of employee IDs to their bookings
 * @param projectFilter - Optional array of selected projects to filter by
 * @returns Total hours booked in the week (filtered if projectFilter is active)
 */
export function calculateWeeklySum(
  employee: IServiceEmployee,
  weekStartDate: Date,
  bookingsByEmployee: Map<number, IBooking[]>,
  projectFilter: IServiceProject[] = [],
): number {
  const start = startOfWeek(weekStartDate, { weekStartsOn: 1 });
  const end = endOfWeek(weekStartDate, { weekStartsOn: 1 });
  const empBookings = bookingsByEmployee.get(employee.id) || [];

  let weekBookings = empBookings.filter(
    (b) => b.date >= start && b.date <= end,
  );

  // If project filter is active, only count matching project hours
  // This matches Vue's bookedProjectHours() behavior
  if (projectFilter.length > 0) {
    const projectIds = new Set(projectFilter.map((p) => p.id));
    weekBookings = weekBookings.filter(
      (b) => b.project && projectIds.has(b.project.id),
    );
  }

  return weekBookings.reduce((sum, b) => sum + (b.hours || 0), 0);
}
